// TOM-673: each configured row says its source and trigger once. This is a
// display-only reduction; titles and authored per-mode values stay untouched.
import type { UnitSettings } from '@neomaris/protocol';

import {
  customAlarmSetup,
  numericThresholdFields,
  perModeThresholds,
  readCustomSpec,
  watchScheduleSentence,
  type AlarmDefView,
  type ConfiguredAlarmView,
} from './alarmDisplay';
import { conditionsClause, readConditions } from './alarmSentence';
import { signalLabel } from './signalCatalog';

export interface AlarmRowSummaryInput {
  readonly def: AlarmDefView | undefined;
  readonly view: ConfiguredAlarmView;
  readonly sourceLabel: string | undefined;
  readonly triggerPhrase: string | null;
  readonly triggerShown: boolean;
  readonly unit: string | null | undefined;
  readonly units: UnitSettings;
  readonly labelFor: (path: string) => string;
  readonly unitFor: (path: string) => string | null | undefined;
}

export interface AlarmRowSummary {
  readonly title: string;
  readonly source: string | null;
  readonly details: string[];
}

interface ModeValues {
  readonly mode: string;
  readonly values: Record<string, unknown>;
}

function modeValues(def: AlarmDefView): ModeValues[] {
  const modes = def.enabledModes.length > 0 ? def.enabledModes : Object.keys(def.thresholdsByMode);
  return [...new Set(modes)].flatMap((mode) => {
    const values = def.thresholdsByMode[mode];
    return values !== undefined && values !== null && typeof values === 'object'
      ? [{ mode, values }]
      : [];
  });
}

function numeric(values: Record<string, unknown>, key: string): number | undefined {
  const value = values[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/** Separate equal values from differing ones before formatting or converting. */
function partition(modes: readonly ModeValues[]): {
  common: Record<string, number>;
  varying: ModeValues[];
} {
  const common: Record<string, number> = {};
  const first = modes[0];
  if (first === undefined) return { common, varying: [] };
  for (const { key, value } of numericThresholdFields(first.values)) {
    if (modes.every(({ values }) => numeric(values, key) === value)) common[key] = value;
  }
  return {
    common,
    varying: modes.map(({ mode, values }) => ({
      mode,
      values: Object.fromEntries(
        numericThresholdFields(values)
          .filter(({ key }) => common[key] === undefined)
          .map(({ key, value }) => [key, value])
      ),
    })),
  };
}

function groupedModes(
  modes: readonly ModeValues[],
  format: (values: Record<string, unknown>) => string
): string[] {
  const groups = new Map<string, string[]>();
  for (const { mode, values } of modes) {
    const text = format(values);
    if (text.length === 0) continue;
    const group = groups.get(text) ?? [];
    group.push(mode);
    groups.set(text, group);
  }
  const entries = [...groups];
  const largest = entries.reduce<(typeof entries)[number] | undefined>(
    (best, entry) => (best === undefined || entry[1].length > best[1].length ? entry : best),
    undefined
  );
  if (
    entries.length > 1 &&
    entries.reduce((count, [, group]) => count + group.length, 0) === modes.length &&
    largest !== undefined &&
    entries.every((entry) => entry === largest || entry[1].length < largest[1].length)
  ) {
    const exceptions = entries
      .filter((entry) => entry !== largest)
      .map(([text, modes]) => `${modes.join(', ')}: ${text}`);
    return [`${largest[0]} (${exceptions.join('; ')})`];
  }
  return entries.map(([text, modes]) => `${modes.join(', ')}: ${text}`);
}

function recipeSummary(def: AlarmDefView, units: UnitSettings): string[] {
  const modes = modeValues(def).map(({ mode, values }) => ({
    mode,
    values: Object.fromEntries(
      Object.entries(values).filter(([key, value]) => key !== 'debounceMs' || value !== 0)
    ),
  }));
  const { common, varying } = partition(modes);
  const format = (values: Record<string, unknown>) =>
    perModeThresholds(['summary'], { summary: values }, units)[0]
      ?.thresholds.sort((a, b) => Number(a.key === 'debounceMs') - Number(b.key === 'debounceMs'))
      .map(({ text }) => text)
      .join(', ') ?? '';
  return [format(common), ...groupedModes(varying, format)].filter(Boolean);
}

const TRIGGER_VERBS: Readonly<Record<string, string>> = {
  High: 'goes above',
  Low: 'drops below',
  On: 'turns on',
  Off: 'turns off',
  DataUnavailable: 'stops reporting for',
};

function customSummary(
  input: AlarmRowSummaryInput
): { generatedTitle: boolean; details: string[] } | null {
  const { def, labelFor, unitFor, units, unit } = input;
  if (def === undefined) return null;
  const spec = readCustomSpec(def);
  if (spec === undefined) return null;
  const options = { labelFor, unitFor, unit, units };
  const setup = customAlarmSetup(def, options);
  if (setup === null) return null;
  const prefix = TRIGGER_VERBS[spec.triggerClass];
  const generatedTitle =
    prefix !== undefined &&
    [setup.signalName, signalLabel(spec.path), input.sourceLabel]
      .filter((label): label is string => label !== undefined && label.trim().length > 0)
      .some((label) => {
        const generatedPrefix = `${label} ${prefix}`;
        const title = def.title.trim();
        return title === generatedPrefix || title.startsWith(`${generatedPrefix} `);
      });
  const modes = modeValues(def);
  const { common, varying } = partition(modes);
  // Compare effective descriptions as well: an absent zero delay or an ignored
  // DataUnavailable delay is not a meaningful difference between modes.
  const hasVariation =
    new Set(
      modes.map(
        ({ mode, values }) =>
          customAlarmSetup(
            { ...def, enabledModes: [mode], thresholdsByMode: { [mode]: values } },
            options
          )?.sentence
      )
    ).size > 1;
  if (!hasVariation) {
    // The prefix is generated by the same helper, so this removes only the
    // source name; it never guesses which part of an owner's title is a name.
    const sentence = setup.sentence;
    return {
      generatedTitle,
      details: [
        sentence.startsWith(`${setup.signalName} `)
          ? sentence.slice(setup.signalName.length + 1)
          : sentence,
      ],
    };
  }

  const fragment = (values: Record<string, unknown>, primary: boolean): string => {
    const partial = customAlarmSetup(
      {
        ...def,
        enabledModes: ['summary'],
        thresholdsByMode: { summary: values },
        conditions: undefined,
      },
      options
    );
    if (partial === null) return '';
    const parts: string[] = [];
    if (primary) parts.push(partial.firesWhen);
    const setpoint = numeric(values, 'setpoint') ?? numeric(common, 'setpoint');
    const clearpoint = numeric(values, 'clearpoint');
    if (
      clearpoint !== undefined &&
      clearpoint !== setpoint &&
      ['High', 'Low'].includes(spec.triggerClass)
    ) {
      parts.push(
        `Clears when it ${partial.clearsWhen.charAt(0).toLowerCase()}${partial.clearsWhen.slice(1)}`
      );
    }
    if ((numeric(values, 'setDelayMs') ?? 0) > 0 || (numeric(values, 'clearDelayMs') ?? 0) > 0) {
      if (partial.delays !== undefined) parts.push(partial.delays);
    }
    return parts.join(', ');
  };
  const primaryKey = spec.triggerClass === 'DataUnavailable' ? 'staleMs' : 'setpoint';
  const numericPrimary = ['High', 'Low', 'DataUnavailable'].includes(spec.triggerClass);
  const commonPrimary = !numericPrimary || numeric(common, primaryKey) !== undefined;
  const details = [
    fragment(common, commonPrimary),
    ...groupedModes(varying, (values) =>
      fragment(values, !commonPrimary || numeric(values, primaryKey) !== undefined)
    ),
  ].filter(Boolean);
  const condition = conditionsClause(readConditions(def.conditions), { labelFor, unitFor });
  if (condition.length > 0) details.push(condition);
  return { generatedTitle, details };
}

export function alarmRowSummary(input: AlarmRowSummaryInput): AlarmRowSummary {
  const { def, view, triggerShown, triggerPhrase } = input;
  const custom = customSummary(input);
  const label =
    input.sourceLabel?.trim() ||
    view.watching?.trim() ||
    (def === undefined
      ? undefined
      : customAlarmSetup(def, { labelFor: input.labelFor })?.signalName);
  const title = custom?.generatedTitle && label ? label : view.title;
  const normalizedName = (name: string) => name.trim().replace(/\s+/g, ' ').toLowerCase();
  const source =
    label === undefined || normalizedName(label) === normalizedName(title) ? null : label;
  if (def === undefined) {
    const details = [view.customSentence, view.modeSummary, view.conditionSummary].filter(
      (text): text is string => text !== undefined && text.length > 0
    );
    if (details.length === 0 && !triggerShown && triggerPhrase) details.push(triggerPhrase);
    return { title, source, details };
  }
  const schedule = watchScheduleSentence(def?.enabledModes ?? view.enabledModes);
  const details = custom?.details ?? recipeSummary(def, input.units);
  const hasNumericTrigger = modeValues(def).some(({ values }) =>
    numericThresholdFields(values).some(
      ({ key }) =>
        !['debounceMs', 'maxRunMs', 'cycleWindowMs', 'staleMs', 'offlineMs'].includes(key)
    )
  );
  if (
    custom === null &&
    !triggerShown &&
    triggerPhrase &&
    (!hasNumericTrigger ||
      ['alarms when on', 'alarms when off', 'alarms if it stops reporting'].includes(triggerPhrase))
  ) {
    details.unshift(triggerPhrase);
  }
  if (custom === null && view.conditionSummary) details.push(view.conditionSummary);
  // A future/older definition may expose only its compiled phrase. Use it
  // rather than invent a trigger from an empty or unreadable threshold map.
  if (details.length === 0 && !triggerShown && triggerPhrase) details.push(triggerPhrase);
  if (schedule.length > 0) details.push(schedule);
  return { title, source, details };
}
